import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, CheckCircle, Search } from 'lucide-react';

const MultiSelect = ({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select options',
  disabled = false,
  className = '',
  maxHeight = '300px',
  searchable = true,
  showSelectedCount = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  // Ensure value is always an array
  const selectedValues = Array.isArray(value) ? value : [];

  // Filter options based on search query
  const filteredOptions = searchable && searchQuery
    ? options.filter(option => {
        const label = typeof option === 'object' ? option.label : option;
        return label.toLowerCase().includes(searchQuery.toLowerCase());
      })
    : options;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current && searchable) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, searchable]);

  // Calculate dropdown position and update on scroll/resize
  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownMenuRef.current) {
      const updatePosition = () => {
        if (!buttonRef.current || !dropdownMenuRef.current) return;
        
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownElement = dropdownMenuRef.current;
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 300; // Approximate max height
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Check if there's enough space below, if not, open upward
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          // Open upward
          dropdownElement.style.position = 'fixed';
          dropdownElement.style.bottom = `${viewportHeight - buttonRect.top + 8}px`;
          dropdownElement.style.top = 'auto';
          dropdownElement.style.left = `${buttonRect.left}px`;
          dropdownElement.style.width = `${buttonRect.width}px`;
          dropdownElement.style.maxHeight = `${Math.min(dropdownHeight, spaceAbove - 16)}px`;
        } else {
          // Open downward (default)
          dropdownElement.style.position = 'fixed';
          dropdownElement.style.top = `${buttonRect.bottom + 8}px`;
          dropdownElement.style.bottom = 'auto';
          dropdownElement.style.left = `${buttonRect.left}px`;
          dropdownElement.style.width = `${buttonRect.width}px`;
          dropdownElement.style.maxHeight = `${Math.min(dropdownHeight, spaceBelow - 16)}px`;
        }
      };
      
      updatePosition();
      
      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, filteredOptions.length]);

  const handleToggle = (optionValue) => {
    if (disabled) return;
    
    const currentValues = [...selectedValues];
    const index = currentValues.indexOf(optionValue);
    
    if (index > -1) {
      // Remove if already selected
      currentValues.splice(index, 1);
    } else {
      // Add if not selected
      currentValues.push(optionValue);
    }
    
    onChange(currentValues);
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    const currentValues = selectedValues.filter(v => v !== optionValue);
    onChange(currentValues);
  };

  const getOptionLabel = (option) => {
    return typeof option === 'object' ? option.label : option;
  };

  const getOptionValue = (option) => {
    return typeof option === 'object' ? option.value : option;
  };

  const getSelectedLabels = () => {
    return selectedValues.map(val => {
      const option = options.find(opt => getOptionValue(opt) === val);
      return option ? getOptionLabel(option) : val;
    });
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Button/Input */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full min-h-[42px] px-4 py-2 bg-background border border-border rounded-lg text-left text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 flex items-center gap-2 ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/50'
        } ${isOpen ? 'ring-2 ring-primary border-primary' : ''}`}
      >
        <div className="flex-1 flex flex-wrap gap-1.5 items-center min-w-0">
          {selectedValues.length === 0 ? (
            <span className="text-muted-foreground text-sm">{placeholder}</span>
          ) : (
            <>
              {getSelectedLabels().slice(0, 3).map((label, idx) => {
                const val = selectedValues[idx];
                return (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-md font-medium"
                  >
                    {label}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => handleRemove(val, e)}
                        className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                );
              })}
              {selectedValues.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{selectedValues.length - 3} more
                </span>
              )}
            </>
          )}
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu using Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownMenuRef}
          className="fixed z-[99999] bg-card/95 backdrop-blur-md border border-border/60 rounded-lg shadow-elegant overflow-hidden dropdown-open"
          style={{ maxHeight }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="p-2 border-b border-border/60">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search languages..."
                  className="w-full pl-9 pr-3 py-2 bg-background/50 border border-border/60 rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {/* Options List */}
          <div className={`overflow-y-auto custom-scrollbar ${searchable ? 'max-h-[250px]' : 'max-h-[300px]'}`}>
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No options found
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = selectedValues.includes(optionValue);
                const isDisabled = typeof option === 'object' && option.disabled;

                return (
                  <button
                    key={optionValue || index}
                    type="button"
                    onClick={() => !isDisabled && handleToggle(optionValue)}
                    disabled={isDisabled}
                    className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 hover:bg-primary/10 hover:text-primary flex items-center justify-between group dropdown-item ${
                      isSelected 
                        ? 'bg-primary/10 text-primary' 
                        : isDisabled
                        ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                        : 'text-foreground'
                    }`}
                    style={{
                      animationDelay: `${index * 0.02}s`
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected 
                          ? 'bg-primary border-primary' 
                          : 'border-border group-hover:border-primary'
                      }`}>
                        {isSelected && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="truncate">{optionLabel}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Selected Count Footer */}
          {showSelectedCount && selectedValues.length > 0 && (
            <div className="px-4 py-2 border-t border-border/60 bg-muted/30 text-xs text-muted-foreground">
              {selectedValues.length} {selectedValues.length === 1 ? 'language' : 'languages'} selected
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default MultiSelect;

